// "Emails sent" — every email Flow sent for this document, with To, CC and
// BCC (Stephanie 2026-10-28: "i want to see everyone that is BCC'ed not just
// myself"). Email hides BCC from every reader, so her own copy can't list the
// others; Flow's record of the send can.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type SentEmailDoc = "po" | "order" | "invoice" | "presentation";

export interface SentEmail {
  id: string;
  subject: string;
  sentAt: string | null;
  delivered: boolean;
  from: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
}

/** Query key — invalidate ["sent-emails"] after any send so the list updates. */
export const sentEmailsKey = (doc: SentEmailDoc, id: string) => ["sent-emails", doc, id];

const SHOW_FIRST = 5;

/** Pure: the "To / CC / BCC" lines one sent email shows (empty ones left out). */
export function sentEmailLines(e: Pick<SentEmail, "to" | "cc" | "bcc">): Array<{ label: string; people: readonly string[] }> {
  return (
    [
      ["To", e.to],
      ["CC", e.cc],
      ["BCC", e.bcc],
    ] as const
  )
    .filter(([, list]) => list.length > 0)
    .map(([label, list]) => ({ label, people: list }));
}

export function SentEmailsCard({ doc, id, className }: { doc: SentEmailDoc; id: string; className?: string }) {
  const [showAll, setShowAll] = useState(false);
  const q = useQuery({
    queryKey: sentEmailsKey(doc, id),
    queryFn: () => api<{ emails: SentEmail[] }>(`/sent-emails/${doc}/${id}`),
    enabled: Boolean(id),
  });
  const emails = q.data?.emails ?? [];
  const shown = showAll ? emails : emails.slice(0, SHOW_FIRST);
  return (
    <section className={className ?? "rounded-md border border-rule bg-white"} aria-labelledby={`sent-emails-${doc}`}>
      <h2 id={`sent-emails-${doc}`} className="px-[22px] pb-3 pt-5 text-lg font-semibold tracking-[-0.4px] text-ink">
        Emails sent
      </h2>
      {q.isLoading ? (
        <p className="border-t border-rule p-5 text-center text-sm text-muted">Loading…</p>
      ) : q.isError ? (
        <p className="border-t border-rule p-5 text-center text-sm text-rust-600">Couldn't load the emails sent.</p>
      ) : emails.length === 0 ? (
        <p className="border-t border-rule p-5 text-center text-sm text-muted">No emails sent from Flow yet.</p>
      ) : (
        <>
          <ul className="divide-y divide-[#e7e7e4] border-t border-rule">
            {shown.map((e) => (
              <li key={e.id} className="px-[22px] py-4">
                <p className="text-sm font-medium text-ink">{e.subject}</p>
                <p className="mt-0.5 text-sm text-muted">
                  {e.sentAt ? new Date(e.sentAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : ""}
                  {e.from ? `${e.sentAt ? " · " : ""}from ${e.from}` : ""}
                  {!e.delivered && <span className="text-rust-600">{e.sentAt || e.from ? " · " : ""}didn't go out</span>}
                </p>
                <dl className="mt-2 space-y-1 text-sm">
                  {sentEmailLines(e).map((l) => (
                    <div key={l.label} className="flex gap-2">
                      <dt className="w-10 shrink-0 font-semibold text-ink">{l.label}</dt>
                      <dd className="min-w-0 break-words text-ink">
                        {l.people.map((who) => (
                          <span key={who} className="block">{who}</span>
                        ))}
                      </dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
          {emails.length > SHOW_FIRST && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="w-full border-t border-rule px-[22px] py-3 text-left text-sm font-medium text-ink hover:bg-peach-50"
            >
              {showAll ? "Show fewer" : `Show all ${emails.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
